import React, { useState } from 'react';
import { Plus, X, CheckCircle, Clock, AlertCircle, Filter } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import './AdminPages.css';

const PRIORITY_BADGE = { High:'badge-danger', Medium:'badge-warning', Low:'badge-gray' };
const STATUS_BADGE   = { Todo:'badge-gray', 'In Progress':'badge-info', Done:'badge-success', Blocked:'badge-danger' };

export default function TasksPage() {
  const { data, commitData, showToast } = useApp();
  const { user, isMentor, isFounder } = useAuth();

  const [filter, setFilter]     = useState('All');
  const [projFilter, setProjFilter] = useState('All');
  const [showAdd, setShowAdd]   = useState(false);
  const defaultAssignee = () => user?.employeeId || data.employees.find(e => e.role === 'employee')?.id || '';
  const [form, setForm]         = useState({ title:'', description:'', projectId:'', assignedTo:'', priority:'Medium', due:'', status:'Todo' });

  // Mentor/founder sees all tasks; employees see their own
  const myTasks = isFounder || isMentor
    ? data.tasks
    : data.tasks.filter(t => String(t.assignedTo) === String(user?.employeeId));

  const filtered = myTasks.filter(t => {
    const sf = filter === 'All' || t.status === filter;
    const pf = projFilter === 'All' || String(t.projectId) === String(projFilter);
    return sf && pf;
  });

  const toggleDone = async (task) => {
    const newStatus = task.status === 'Done' ? 'In Progress' : 'Done';
    try {
      await commitData('tasks', data.tasks.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
      showToast(newStatus === 'Done' ? 'Task marked complete!' : 'Task reopened');
    } catch {}
  };

  const updateStatus = async (id, status) => {
    try {
      await commitData('tasks', data.tasks.map(t => t.id === id ? { ...t, status } : t));
      showToast(`Task status updated to ${status} — assignee emailed when mail is configured`);
    } catch {}
  };

  const deleteTask = async (id) => {
    try {
      await commitData('tasks', data.tasks.filter(t => t.id !== id));
      showToast('Task deleted', 'warning');
    } catch {}
  };

  const addTask = async () => {
    if (!form.title || !form.projectId) { showToast('Title and project are required','error'); return; }
    const assignTo = form.assignedTo || defaultAssignee();
    try {
      await commitData('tasks', [...data.tasks, { id:String(Date.now()), ...form, projectId:form.projectId, assignedTo:assignTo }]);
      showToast('Task created — assignee notified by email when mail is configured');
      setShowAdd(false);
      setForm({ title:'',description:'',projectId:'',assignedTo:defaultAssignee(),priority:'Medium',due:'',status:'Todo' });
    } catch {}
  };

  const getProjectName = (id) => data.projects.find(p => String(p.id) === String(id))?.name || 'Unknown';
  const getEmpName = (id) => data.employees.find(e => String(e.id) === String(id))?.name || 'Unknown';

  const done = myTasks.filter(t=>t.status==='Done').length;
  const inProgress = myTasks.filter(t=>t.status==='In Progress').length;

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{isMentor || isFounder ? 'All Tasks' : 'My Tasks'}</h1>
          <p className="page-subtitle">{myTasks.length} tasks · {done} completed · {inProgress} in progress</p>
        </div>
        <div className="page-actions">
          {(isMentor || isFounder) && (
            <button className="btn-gold" onClick={()=>{ setForm({ title:'',description:'',projectId:'',assignedTo:defaultAssignee(),priority:'Medium',due:'',status:'Todo' }); setShowAdd(true); }}><Plus size={15}/> Add Task</button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="stats-row" style={{ marginBottom:20 }}>
        {[
          { label:'Total',      value:myTasks.length,  color:'var(--secondary-color)' },
          { label:'Todo',       value:myTasks.filter(t=>t.status==='Todo').length,        color:'var(--text-muted)' },
          { label:'In Progress',value:inProgress,      color:'var(--info)' },
          { label:'Completed',  value:done,            color:'var(--success)' },
        ].map(s=>(
          <div key={s.label} className="stat-card">
            <div className="stat-value" style={{ color:s.color,fontSize:'1.8rem' }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="filter-bar">
        <div className="filter-tabs">
          {['All','Todo','In Progress','Done','Blocked'].map(s=>(
            <button key={s} className={`filter-tab ${filter===s?'active':''}`} onClick={()=>setFilter(s)}>{s}</button>
          ))}
        </div>
        <select className="form-input" style={{ width:'auto',minWidth:160 }} value={projFilter} onChange={e=>setProjFilter(e.target.value)}>
          <option value="All">All Projects</option>
          {data.projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
        {filtered.map(task => (
          <div key={task.id} className="task-item">
            <div className={`task-cb ${task.status==='Done'?'done':''}`} onClick={()=>toggleDone(task)}>
              {task.status==='Done' && <CheckCircle size={12} color="#fff"/>}
            </div>
            <div style={{ flex:1 }}>
              <p className={`task-title ${task.status==='Done'?'done':''}`}>{task.title}</p>
              {task.description && <p style={{ fontSize:'0.75rem',color:'var(--text-muted)',marginTop:2 }}>{task.description}</p>}
              <div style={{ display:'flex',gap:10,marginTop:6,flexWrap:'wrap',fontSize:'0.73rem',color:'var(--text-muted)' }}>
                <span>📁 {getProjectName(task.projectId)}</span>
                {(isMentor||isFounder) && <span>👤 {getEmpName(task.assignedTo)}</span>}
                {task.due && <span>📅 {new Date(task.due).toLocaleDateString('en-ZA')}</span>}
              </div>
            </div>
            <div style={{ display:'flex',gap:6,alignItems:'center' }}>
              <span className={`badge ${PRIORITY_BADGE[task.priority]}`}>{task.priority}</span>
              <span className={`badge ${STATUS_BADGE[task.status]}`}>{task.status}</span>
              {/* Status change dropdown for employee */}
              {task.status !== 'Done' && (
                <select
                  className="form-input"
                  style={{ width:'auto',padding:'4px 8px',fontSize:'0.72rem',height:'28px' }}
                  value={task.status}
                  onChange={e=>updateStatus(task.id,e.target.value)}
                >
                  {['Todo','In Progress','Blocked'].map(s=><option key={s}>{s}</option>)}
                </select>
              )}
              {(isMentor||isFounder) && (
                <button className="btn-ghost" style={{ padding:'4px 6px',color:'var(--danger)' }} onClick={()=>deleteTask(task.id)}>×</button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign:'center',padding:'48px',color:'var(--text-dim)' }}>
            <CheckCircle size={36} style={{ marginBottom:12,opacity:0.3 }}/>
            <p>No {filter === 'All' ? '' : filter.toLowerCase()+' '}tasks found</p>
          </div>
        )}
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={()=>setShowAdd(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">Add Task</p>
              <button className="modal-close" onClick={()=>setShowAdd(false)}><X size={18}/></button>
            </div>
            <div className="modal-form">
              <div className="form-group"><label className="form-label">Task Title *</label><input className="form-input" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Task name"/></div>
              <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Details..."/></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Project *</label>
                  <select className="form-input" value={form.projectId} onChange={e=>setForm({...form,projectId:e.target.value})}>
                    <option value="">Select project...</option>
                    {data.projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Assign To</label>
                  <select className="form-input" value={form.assignedTo} onChange={e=>setForm({...form,assignedTo:e.target.value})}>
                    {data.employees.filter(e=>e.role==='employee').map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Priority</label>
                  <select className="form-input" value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})}>
                    {['High','Medium','Low'].map(p=><option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Due Date</label><input className="form-input" type="date" value={form.due} onChange={e=>setForm({...form,due:e.target.value})}/></div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>setShowAdd(false)}>Cancel</button>
              <button className="btn-gold" onClick={addTask}>Create Task</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
